import { useEffect, useState } from "react";
import api from "@/lib/api";
import BookButton from "@/components/BookButton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PROPERTY } from "@/lib/constants";
import { useSEO, breadcrumbLd } from "@/lib/seo";

export default function Amenities() {
  const [amenities, setAmenities] = useState([]);
  useEffect(() => {
    api.get("/amenities").then((r) => setAmenities(r.data)).catch(() => {});
  }, []);

  useSEO({
    title: "Amenities & Policies — Free Breakfast, Wi-Fi, Pet Policy",
    description:
      "Free hot breakfast, complimentary Wi-Fi, pet-friendly rooms, truck parking, jacuzzi & family suites. Transparent policies — no junk fees, no surprises.",
    path: "/amenities",
    jsonLd: breadcrumbLd([{ name: "Home", path: "/" }, { name: "Amenities", path: "/amenities" }]),
  });

  return (
    <div data-testid="amenities-page">
      <section className="pt-36 pb-16 bg-canvas">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <p className="font-mono-label text-ember mb-4">Amenities &amp; Policies</p>
            <h1 className="font-display text-6xl md:text-7xl text-ink leading-[0.98]">
              Everything in.<br /><span className="italic">Nothing hidden.</span>
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-ink-soft leading-relaxed">
              We list every fee, every policy, and every amenity here — exactly as you'll experience it
              when you check in. No junk fees, no surprises.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-canvas pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-x-12 gap-y-8">
          {amenities.map((a, i) => (
            <div key={a.id} className="grid grid-cols-[auto_1fr] gap-5 border-t border-rule-strong pt-6 fade-up" style={{ animationDelay: `${i * 50}ms` }} data-testid={`amenity-row-${i}`}>
              <p className="font-display text-4xl text-ember leading-none w-12">{String(i + 1).padStart(2, "0")}</p>
              <div>
                <h3 className="font-display text-2xl text-ink leading-tight">{a.title}</h3>
                <p className="text-ink-soft mt-2 leading-relaxed">{a.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-sand py-24" data-testid="policies-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="font-mono-label text-ember mb-4">Policies</p>
            <h2 className="font-display text-5xl text-ink leading-tight">
              Transparent by default.<br /><span className="italic">Always.</span>
            </h2>
            <p className="mt-6 text-ink-soft leading-relaxed">
              Our rates are inclusive of taxes shown at booking. There are no resort fees, destination
              fees, or hidden surcharges at Westbridge — the price you see is the price you pay.
            </p>
          </div>
          <div className="md:col-span-7">
            <Accordion type="single" collapsible defaultValue="check-in" data-testid="policy-accordion">
              <AccordionItem value="check-in" className="border-b border-rule-strong">
                <AccordionTrigger className="font-display text-2xl text-ink py-6">
                  Check-in &amp; check-out times
                </AccordionTrigger>
                <AccordionContent className="text-ink-soft leading-relaxed pb-6">
                  Check-in: {PROPERTY.checkIn} · Check-out: {PROPERTY.checkOut}. Early check-in and late
                  check-out are available on request and subject to availability — please call ahead.
                  The front desk is staffed 24 hours; late arrivals are always welcome.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="cancellation" className="border-b border-rule-strong">
                <AccordionTrigger className="font-display text-2xl text-ink py-6">Cancellation</AccordionTrigger>
                <AccordionContent className="text-ink-soft leading-relaxed pb-6">
                  Reservations booked direct may be cancelled free of charge up to 24 hours before the
                  scheduled check-in time. Cancellations inside that window are charged for one night's
                  stay. Reservations through third-party sites follow that provider's policy — book
                  direct for the most flexible terms.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="pets" className="border-b border-rule-strong">
                <AccordionTrigger className="font-display text-2xl text-ink py-6">Pet policy</AccordionTrigger>
                <AccordionContent className="text-ink-soft leading-relaxed pb-6">
                  Service animals are always welcome at no additional charge. Pet-friendly rooms are
                  available with a limit of two pets per room; a pet fee is collected at check-in. Pets
                  must not be left unattended in the room.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="ada" className="border-b border-rule-strong">
                <AccordionTrigger className="font-display text-2xl text-ink py-6">Accessibility</AccordionTrigger>
                <AccordionContent className="text-ink-soft leading-relaxed pb-6">
                  Accessible queen rooms with roll-in showers and visual alerts are available — see
                  the Accessibility page or call us and we'll set everything up before you arrive.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="smoking" className="border-b border-rule-strong">
                <AccordionTrigger className="font-display text-2xl text-ink py-6">Smoking</AccordionTrigger>
                <AccordionContent className="text-ink-soft leading-relaxed pb-6">
                  All guest rooms and indoor common areas are 100% smoke-free. Designated outdoor smoking
                  areas are available on the property. A cleaning fee applies for smoking in non-designated areas.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="privacy" className="border-b border-rule-strong">
                <AccordionTrigger className="font-display text-2xl text-ink py-6">Privacy policy</AccordionTrigger>
                <AccordionContent className="text-ink-soft leading-relaxed pb-6">
                  We collect only the information necessary to process your reservation and provide
                  service during your stay. We do not sell, rent, or share your personal information
                  with third parties for marketing. Full policy details available on request.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      <section className="bg-ink text-canvas py-20" data-testid="amenities-cta">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h2 className="font-display text-5xl md:text-6xl leading-[1.02]">
              Questions before you book?
            </h2>
            <p className="mt-6 text-canvas/80 text-lg">Call our 24-hour front desk — we'll answer.</p>
          </div>
          <div className="md:col-span-4 md:text-right flex flex-col md:items-end gap-4">
            <BookButton label="Reserve Now" testid="amenities-cta-book" />
            <a href={`tel:${PROPERTY.phone.replace(/[^\d+]/g, "")}`} className="link-underline text-canvas font-mono-label" data-testid="amenities-cta-phone">
              {PROPERTY.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
